import { Trans, useLingui } from '@lingui/react/macro';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { CHAT_FEATURE_REQUIRED_MESSAGE } from 'modules/chat/domain/constants';
import ChatComposer from 'modules/chat/ui/components/chat-composer';
import ChatQuotaBar from 'modules/chat/ui/components/chat-quota-bar';
import ChatTranscript from 'modules/chat/ui/components/chat-transcript';
import { useChat } from 'modules/chat/ui/hooks/use-chat';
import { getChatErrorText } from 'modules/chat/ui/utils';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useFeature } from 'shared/hooks/use-feature';
import { i18n } from 'shared/i18n';
import { theme } from 'styles/theme/theme';
import { UDButton, UDText } from 'ud-ui/components';
import UDHeaderTitle from 'ud-ui/components/header-title';

const ChatPage: React.FC = () => {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();
  const { isEnabled, isFetching } = useFeature('AI_CHAT_ASSISTANT');
  const isLocked = !isFetching && !isEnabled;
  const { messages, isSending, isClearing, error, quota, sendMessage, clearHistory, confirmWrite, cancelWrite } =
    useChat({ enabled: isEnabled });

  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>ИИ-ассистент</Trans>
      </UDHeaderTitle>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  // Тост показывается один раз на ошибку: `i18n` — глобальный, чтобы смена языка не повторяла тост.
  useEffect(() => {
    if (error) {
      toast.error(getChatErrorText(error, (descriptor) => i18n._(descriptor)));
    }
  }, [error]);

  if (isLocked) {
    return (
      <div className="flex h-full w-full flex-col gap-3 px-6 py-6">
        <div className="flex flex-1 flex-col items-center justify-center overflow-hidden rounded-lg bg-white px-6 text-center">
          <UDText type="body-text" color={theme.colors.Gray}>
            {t(CHAT_FEATURE_REQUIRED_MESSAGE)}
          </UDText>
        </div>
        <ChatComposer disabled onSubmit={async () => undefined} />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-3 px-6 py-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <ChatQuotaBar quota={quota} />
        </div>
        <UDButton variant="secondary" slim onClick={clearHistory} loading={isClearing} disabled={messages.length === 0}>
          <Trans>Очистить историю</Trans>
        </UDButton>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden rounded-lg bg-white">
        <ChatTranscript
          messages={messages}
          isSending={isSending}
          onConfirmWrite={confirmWrite}
          onCancelWrite={cancelWrite}
        />
      </div>

      <ChatComposer disabled={isSending || isFetching} onSubmit={sendMessage} />
    </div>
  );
};

export default ChatPage;
